/**
 * InstitutionPortalDemo — read-only demo institution console.
 *
 * TechSprint surface that shows exactly what information a participating
 * institution provides to Altara and HOW each piece is verified. This
 * is a public, no-auth demo view — a full self-serve editor is out of
 * scope for this build.
 *
 * The page is organised as five panels:
 *   1. Institution header + Trust Utility summary
 *   2. What we collect · Institution identity
 *   3. What we collect · Communication channels (with per-channel
 *      provenance and verification indicators)
 *   4. What we collect · Representatives (with association + regulatory
 *      validation state)
 *   5. How verification works · provider abstraction + boundary notes
 *
 * The goal is to make the "field-by-field provenance" story visible
 * without exposing consumer PII. Only aggregate counts are shown.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useParams } from "react-router-dom";
import {
  Building2, ShieldCheck, Globe, Mail, Phone, MessageCircle, LifeBuoy,
  ShieldAlert, User, CheckCircle2, Info, ArrowLeft, Database, Search,
  Server, KeyRound, Users, Send, Landmark, ExternalLink, Sparkles,
} from "lucide-react";
import { NAVY, GOLD } from "@/components/wallet/WalletShell";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const CHANNEL_META = {
  WEBSITE:         { icon: Globe,         label: "Official website" },
  DOMAIN:          { icon: Globe,         label: "Official domain" },
  EMAIL_DOMAIN:    { icon: Mail,          label: "Official email domain" },
  PHONE:           { icon: Phone,         label: "Official telephone" },
  WHATSAPP:        { icon: MessageCircle, label: "Official WhatsApp" },
  SMS_SENDER_ID:   { icon: MessageCircle, label: "SMS sender ID" },
  SUPPORT:         { icon: LifeBuoy,      label: "Support channel" },
  FRAUD_REPORTING: { icon: ShieldAlert,   label: "Fraud reporting contact" },
};

const SOURCE_META = {
  INSTITUTION_PROVIDED: { label: "Provided by institution", tone: NAVY,       icon: Building2 },
  ALTARA_RELATIONSHIP:  { label: "Altara-established",       tone: "#059669", icon: ShieldCheck },
  REGULATORY_SOURCE:    { label: "Regulatory source",        tone: "#7C3AED", icon: Search },
  ALTARA_TRANSACTION:   { label: "Received through Altara",  tone: GOLD,      icon: Send },
  CONSUMER_PROVIDED:    { label: "Provided by consumer",     tone: "#B45309", icon: User },
  DEMO:                 { label: "Demo data",                tone: "#94A3B8", icon: Database },
};

export default function InstitutionPortalDemo() {
  const { id } = useParams();
  const [data, setData]   = useState(null);
  const [err,  setErr]    = useState(null);

  useEffect(() => {
    let c = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/institution-portal/${id}`);
        if (!c) setData(data);
      } catch (e) { if (!c) setErr(e?.response?.data?.detail || "Not found."); }
    })();
    return () => { c = true; };
  }, [id]);

  if (err) return <ErrorState msg={err} />;
  if (!data) return <Loading />;

  const inst  = data.institution;
  const regI  = data.regulatory_validation_institution;
  const chans = data.channels || [];
  const reps  = data.representatives || [];
  const agg   = data.aggregate || {};

  return (
    <div className="min-h-screen" style={{ background: "#F8FAFC" }} data-testid="institution-portal">
      {/* Top bar */}
      <div className="border-b" style={{ background: NAVY, color: "white", borderColor: "#0F172A" }}>
        <div className="max-w-5xl mx-auto px-4 lg:px-8 py-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em]" style={{ color: GOLD }}>Altara Trust Utility</div>
            <div className="font-display text-lg font-semibold mt-0.5">Institution Console · Demo</div>
          </div>
          <Link
            to="/wallet/institutions"
            data-testid="portal-back"
            className="inline-flex items-center gap-1.5 text-[12px] font-medium hover:underline"
            style={{ color: GOLD }}
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Consumer view
          </Link>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 lg:px-8 py-6 lg:py-10 space-y-6">
        {/* Institution header */}
        <header className="rounded-2xl border bg-white p-5 lg:p-6" style={{ borderColor: "#E2E8F0" }} data-testid="portal-header">
          <div className="flex items-start gap-4 flex-wrap">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: `${GOLD}22` }}>
              <Building2 className="w-7 h-7" style={{ color: NAVY }} />
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="font-display text-2xl font-semibold" style={{ color: NAVY }}>
                {inst.trading_name || inst.legal_name}
              </h1>
              <div className="text-[12px] text-slate-500 mt-1">
                {inst.jurisdiction} · {inst.institution_type} · regulated by {inst.regulator}
              </div>
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10.5px] font-semibold" style={{ background: "#ECFDF5", color: "#065F46" }}>
                  <ShieldCheck className="w-3 h-3" /> Participating Institution
                </span>
                {regI && (
                  <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10.5px] font-semibold" style={{ background: `${NAVY}12`, color: NAVY }}>
                    <CheckCircle2 className="w-3 h-3" /> Regulatory record matched
                  </span>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 flex-shrink-0">
              <MetricPill label="Connected consumers" value={agg.connected_consumers ?? 0} icon={Users} />
              <MetricPill label="Interactions delivered" value={agg.interactions_delivered ?? 0} icon={Send} />
            </div>
          </div>
          <div className="mt-4 text-[11.5px] text-slate-600 leading-relaxed border-t border-slate-100 pt-3">
            This is a read-only view of the data the institution provides Altara and the verification state of each field. No consumer identities are exposed — only aggregate counts.
          </div>
        </header>

        {/* 1. Identity */}
        <PanelCard title="What we collect · Institution identity" icon={KeyRound} testid="panel-identity">
          <FieldTable
            rows={[
              ["Legal name",             inst.legal_name,             "INSTITUTION_PROVIDED"],
              ["Trading name",           inst.trading_name,           "INSTITUTION_PROVIDED"],
              ["Registration number",    inst.registration_number,    "INSTITUTION_PROVIDED"],
              ["Institution type",       inst.institution_type,       "INSTITUTION_PROVIDED"],
              ["Jurisdiction",           inst.jurisdiction,           "INSTITUTION_PROVIDED"],
              ["Regulator",              inst.regulator,              "INSTITUTION_PROVIDED"],
              ["FSP number",             inst.regulatory_identifier ? `${inst.regulatory_identifier} (demo)` : "—", "INSTITUTION_PROVIDED"],
              ["Participation status",   inst.participation_status,   "ALTARA_RELATIONSHIP"],
            ]}
            regulatoryFooter={regI ? {
              status:  regI.validation_status,
              matched: regI.matched_name,
              via:     regI.provider,
              source:  regI.source_reference,
              at:      regI.checked_at,
            } : null}
          />
        </PanelCard>

        {/* 2. Communication channels */}
        <PanelCard title="What we collect · Communication channels" icon={Server} testid="panel-channels">
          <p className="text-[12px] text-slate-600 leading-relaxed mb-3">
            Every channel is stored with its own provenance, status and last-validated timestamp so the consumer can compare an unexpected interaction against the trusted reference.
          </p>
          <div className="rounded-xl border border-slate-100 overflow-hidden">
            <table className="w-full text-[12px]">
              <thead className="text-[10.5px] uppercase tracking-widest text-slate-500 bg-slate-50">
                <tr>
                  <th className="text-left px-3 py-2">Type</th>
                  <th className="text-left px-3 py-2">Value</th>
                  <th className="text-left px-3 py-2">Provenance</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-left px-3 py-2">Last validated</th>
                </tr>
              </thead>
              <tbody>
                {chans.map(ch => {
                  const meta = CHANNEL_META[ch.channel_type] || { icon: Globe, label: ch.channel_type };
                  const Icon = meta.icon;
                  return (
                    <tr key={ch.channel_id} className="border-t border-slate-100" data-testid={`portal-channel-${ch.channel_type.toLowerCase()}`}>
                      <td className="px-3 py-2">
                        <div className="inline-flex items-center gap-1.5 font-medium" style={{ color: NAVY }}>
                          <Icon className="w-3.5 h-3.5" style={{ color: NAVY }} />
                          {meta.label}
                        </div>
                      </td>
                      <td className="px-3 py-2 font-mono text-[11.5px] break-all" style={{ color: NAVY }}>{ch.channel_value}</td>
                      <td className="px-3 py-2"><ProvenanceChip source={ch.source_type} /></td>
                      <td className="px-3 py-2">
                        <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-semibold" style={{ background: "#ECFDF5", color: "#065F46" }}>
                          <CheckCircle2 className="w-3 h-3" /> {ch.status}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-[10.5px] text-slate-500 font-mono">
                        {ch.last_validated_at ? new Date(ch.last_validated_at).toLocaleDateString() : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </PanelCard>

        {/* 3. Representatives */}
        <PanelCard title="What we collect · Representatives" icon={User} testid="panel-representatives">
          <p className="text-[12px] text-slate-600 leading-relaxed mb-3">
            Each adviser is linked to the institution and, where available, checked against the regulatory-source record. An <em>Associated</em> record proves the institution has attested to the person; a <em>matched</em> record proves the identity details correspond with regulatory information.
          </p>
          <div className="space-y-3">
            {reps.map(r => {
              const regRep = data.regulatory_validation_representatives?.[r.representative_id];
              return (
                <div key={r.representative_id} className="rounded-xl border border-slate-100 p-4" data-testid={`portal-rep-${r.representative_id}`}>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: `${GOLD}22` }}>
                      <User className="w-5 h-5" style={{ color: NAVY }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-display text-[14.5px] font-semibold" style={{ color: NAVY }}>{r.name}</div>
                      <div className="text-[12px] text-slate-600">{r.role}</div>
                      <div className="mt-2 flex items-center gap-2 flex-wrap">
                        <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[10.5px] font-semibold" style={{ background: `${GOLD}22`, color: NAVY }}>
                          Associated with institution
                        </span>
                        {regRep && (
                          <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[10.5px] font-semibold" style={{ background: `${NAVY}12`, color: NAVY }}>
                            <CheckCircle2 className="w-3 h-3" /> Record matched · {regRep.provider}
                          </span>
                        )}
                        <ProvenanceChip source={r.source_type} />
                      </div>
                      <dl className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-[11.5px]">
                        <MiniRow k="Institution reference" v={r.institution_reference} />
                        <MiniRow k="Regulatory identifier" v={r.regulatory_identifier} />
                        <MiniRow k="Email"                  v={r.email} />
                        <MiniRow k="Phone"                  v={r.phone} />
                      </dl>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </PanelCard>

        {/* 4. Verification methods */}
        <PanelCard title="How verification works" icon={ShieldCheck} testid="panel-verification">
          <div className="space-y-3">
            <VerificationRow
              tone={NAVY}
              icon={Building2}
              title="Institution-provided data"
              body="The participating institution provides its identity, digital identity and communication-channel information to Altara. Every field is tagged INSTITUTION_PROVIDED and versioned so historical evidence is preserved when things change."
            />
            <VerificationRow
              tone="#7C3AED"
              icon={Search}
              title={`Regulatory validation · via ${data.provider_info.provider_id}`}
              body={data.provider_info.note}
            />
            <VerificationRow
              tone="#059669"
              icon={ShieldCheck}
              title="Altara-established relationship"
              body="When the institution establishes a relationship with a consumer through Altara, that link is stored as ALTARA_RELATIONSHIP data. Institutions cannot see relationships held by other institutions."
            />
            <VerificationRow
              tone={GOLD}
              icon={Send}
              title="Altara transaction provenance"
              body="Financial interactions actually delivered through Altara are tagged ALTARA_TRANSACTION and can be referenced later by consumers checking suspicious contact."
            />
            <VerificationRow
              tone="#B45309"
              icon={Info}
              title="Boundary of verification"
              body="A matched regulatory record establishes that identity details correspond with the regulator's information. It does NOT authenticate every message, document, person or payment request using the institution's or adviser's name — that is the role of the future Trust & Safety workflow."
            />
          </div>
        </PanelCard>

        <footer className="text-[10.5px] text-slate-500 text-center py-2">
          Institution Console · Demo · No consumer PII is exposed on this surface.
        </footer>
      </div>
    </div>
  );
}

/* ─────────────────────── UI primitives ─────────────────────── */
function PanelCard({ title, icon: Icon, children, testid }) {
  return (
    <section className="rounded-2xl border bg-white p-5 lg:p-6" style={{ borderColor: "#E2E8F0" }} data-testid={testid}>
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${GOLD}22` }}>
          <Icon className="w-4 h-4" style={{ color: NAVY }} />
        </div>
        <div className="text-[10.5px] uppercase tracking-[0.24em] font-semibold" style={{ color: GOLD }}>
          {title}
        </div>
      </div>
      {children}
    </section>
  );
}

function MetricPill({ label, value, icon: Icon }) {
  return (
    <div className="rounded-xl bg-slate-50 border border-slate-100 px-3 py-2">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-semibold text-slate-500">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className="font-mono font-semibold text-[16px] mt-0.5" style={{ color: NAVY }}>{value}</div>
    </div>
  );
}

function FieldTable({ rows, regulatoryFooter }) {
  return (
    <>
      <div className="rounded-xl border border-slate-100 overflow-hidden">
        <table className="w-full text-[12px]">
          <thead className="text-[10.5px] uppercase tracking-widest text-slate-500 bg-slate-50">
            <tr>
              <th className="text-left px-3 py-2">Field</th>
              <th className="text-left px-3 py-2">Value</th>
              <th className="text-left px-3 py-2">Provenance</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(([k, v, src]) => (
              <tr key={k} className="border-t border-slate-100">
                <td className="px-3 py-2 text-slate-600">{k}</td>
                <td className="px-3 py-2 font-medium" style={{ color: NAVY }}>{v || "—"}</td>
                <td className="px-3 py-2"><ProvenanceChip source={src} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {regulatoryFooter && (
        <div className="mt-3 rounded-xl border p-3 flex items-start gap-2" style={{ background: `${NAVY}08`, borderColor: `${NAVY}22` }} data-testid="portal-regulatory-footer">
          <Search className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: NAVY }} />
          <div className="text-[11.5px]">
            <div className="font-semibold" style={{ color: NAVY }}>Regulatory validation cached</div>
            <div className="text-slate-600 mt-0.5 leading-relaxed">
              Provider <span className="font-mono">{regulatoryFooter.via}</span> reported <span className="font-semibold">{regulatoryFooter.status}</span> for "{regulatoryFooter.matched}" at {new Date(regulatoryFooter.at).toLocaleString()}. Source: <em>{regulatoryFooter.source}</em>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function ProvenanceChip({ source }) {
  const meta = SOURCE_META[source] || SOURCE_META.INSTITUTION_PROVIDED;
  const Icon = meta.icon;
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-semibold"
      style={{ background: `${meta.tone}18`, color: meta.tone }}
      data-testid={`prov-${source.toLowerCase()}`}
      title={source}
    >
      <Icon className="w-3 h-3" /> {meta.label}
    </span>
  );
}

function MiniRow({ k, v }) {
  if (!v) return null;
  return (
    <div className="flex items-baseline gap-2">
      <dt className="text-[10.5px] uppercase tracking-widest text-slate-400 w-32 flex-shrink-0">{k}</dt>
      <dd className="font-medium truncate" style={{ color: NAVY }}>{v}</dd>
    </div>
  );
}

function VerificationRow({ tone, icon: Icon, title, body }) {
  return (
    <div className="rounded-xl p-3 border flex items-start gap-3" style={{ background: `${tone}0F`, borderColor: `${tone}33` }}>
      <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-white">
        <Icon className="w-4 h-4" style={{ color: tone }} />
      </div>
      <div className="min-w-0">
        <div className="text-[13px] font-semibold" style={{ color: NAVY }}>{title}</div>
        <div className="text-[11.5px] text-slate-600 mt-0.5 leading-relaxed">{body}</div>
      </div>
    </div>
  );
}

function Loading() {
  return <div className="min-h-screen flex items-center justify-center text-slate-400" data-testid="portal-loading">Loading institution console…</div>;
}
function ErrorState({ msg }) {
  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="max-w-md text-center" data-testid="portal-error">
        <Landmark className="w-8 h-8 mx-auto text-slate-400" />
        <div className="mt-3 font-display text-lg font-semibold" style={{ color: NAVY }}>Institution unavailable</div>
        <div className="text-[13px] text-slate-500 mt-1">{msg}</div>
        <Link to="/wallet/institutions" className="mt-4 inline-flex items-center gap-1 text-[12px] font-medium" style={{ color: NAVY }}>
          ← Back
        </Link>
      </div>
    </div>
  );
}
